import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Eye, Calendar, DollarSign, Filter, Search } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import NotificationBell from '@/components/NotificationBell';


import { HistorySkeleton } from '@/components/LoadingIndicators';

export default function CalculationHistory() {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [filteredScenarios, setFilteredScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterService, setFilterService] = useState('all');
  const [services, setServices] = useState([]);
  const [deleteModal, setDeleteModal] = useState({ show: false, scenario: null });
  const [deleting, setDeleting] = useState(false);
  const userRole = JSON.parse(localStorage.getItem('user') || '{}').role || 'preparer';
  const userId = JSON.parse(localStorage.getItem('user') || '{}').id;

  useEffect(() => {
    fetchScenarios();
    fetchServices();
  }, []);

  // Reload data when returning from Settings/Manager pages
  // Mirrors Calculator's robust reload mechanism with multiple event listeners
  useEffect(() => {
    // Check on mount
    const shouldReload = localStorage.getItem('reload_reference_data');
    if (shouldReload) {
      console.log('[History] Reload flag detected on mount - refreshing data');
      localStorage.removeItem('reload_reference_data');
      fetchScenarios();
      fetchServices();
    }

    // Handle storage events (cross-tab/window communication)
    const handleStorageChange = (e) => {
      if (e.key === 'reload_reference_data') {
        console.log('[History] Storage event detected - refreshing data');
        fetchScenarios();
        fetchServices();
        localStorage.removeItem('reload_reference_data');
      }
    };

    // Handle window focus (user returns to this tab/window)
    const handleFocus = () => {
      const shouldReload = localStorage.getItem('reload_reference_data');
      if (shouldReload) {
        console.log('[History] Focus detected with reload flag - refreshing data');
        fetchScenarios();
        fetchServices();
        localStorage.removeItem('reload_reference_data');
      }
    };

    // Handle visibility change (tab becomes visible)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const shouldReload = localStorage.getItem('reload_reference_data');
        if (shouldReload) {
          console.log('[History] Page became visible with reload flag - refreshing data');
          fetchScenarios();
          fetchServices();
          localStorage.removeItem('reload_reference_data');
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Set reload flag when leaving this page (to refresh other pages)
  useEffect(() => {
    return () => {
      // Cleanup: Set flags for other pages to reload when user navigates away
      console.log('[History] Setting reload flags for Proposals and Calculator on unmount');
      localStorage.setItem('reload_proposals', 'true');
      localStorage.setItem('reload_calculator', 'true');
    };
  }, []);

  useEffect(() => {
    applyFilters();
  }, [scenarios, searchTerm, filterStatus, filterService]);

  const fetchScenarios = async () => {
    try {
      setLoading(true);
      const response = await api.get('/scenarios');
      setScenarios(response.data.scenarios || []);
    } catch (error) {
      console.error('Error fetching scenarios:', error);
      toast.error('Failed to load calculation history');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await api.get('/admin/services');
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...scenarios];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(s =>
        s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.service_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(s => s.status === filterStatus);
    }

    // Service filter
    if (filterService !== 'all') {
      filtered = filtered.filter(s => s.service_id === filterService);
    }

    setFilteredScenarios(filtered);
  };

  const handleDelete = async (scenarioId, scenarioName) => {
    setDeleteModal({ show: true, scenario: { id: scenarioId, name: scenarioName } });
  };

  const confirmDelete = async () => {
    const { id: scenarioId } = deleteModal.scenario;
    
    try {
      setDeleting(true);
      await api.delete(`/scenarios/${scenarioId}`);
      // Immediately remove from local state for instant UI feedback
      setScenarios(prev => prev.filter(s => s.id !== scenarioId));
      toast.success('Calculation deleted successfully');
      setDeleteModal({ show: false, scenario: null });
    } catch (error) {
      console.error('Error deleting scenario:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete calculation');
    } finally {
      setDeleting(false);
    }
  };

  const handleView = (scenarioId) => {
    // Navigate to calculator with scenario loaded
    navigate(`/calculator?scenario=${scenarioId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft' },
      pending_approval: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending Approval' },
      pending_cfo_approval: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pending CFO' },
      pending_ceo_approval: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Pending CEO' },
      approved: { bg: 'bg-green-100', text: 'text-green-700', label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' }
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 animate-fade-in">
      {/* Glass Header */}
      <div className="glass-card sticky top-0 z-10 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 text-heading-tight">Calculation History</h1>
                <p className="text-sm text-slate-500 font-medium mt-1">View and manage saved calculations</p>
              </div>
            </div>
            <NotificationBell />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="card-modern p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {/* Search */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Search className="w-4 h-4 inline mr-2" />
                Search
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or service..."
                className="input-field w-full"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Filter className="w-4 h-4 inline mr-2" />
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="input-field w-full"
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="pending_cfo_approval">Pending CFO</option>
                <option value="pending_ceo_approval">Pending CEO</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Service Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Service</label>
              <select
                value={filterService}
                onChange={(e) => setFilterService(e.target.value)}
                className="input-field w-full"
              >
                <option value="all">All Services</option>
                {services.map(service => (
                  <option key={service.id} value={service.id}>{service.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <HistorySkeleton />
        ) : filteredScenarios.length === 0 ? (
          <div className="card p-12 text-center">
            <DollarSign className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 text-lg font-medium">No calculations found</p>
            <p className="text-slate-500 text-sm mt-2">
              {searchTerm || filterStatus !== 'all' || filterService !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first calculation to get started'}
            </p>
            <button
              onClick={() => navigate('/calculator')}
              className="btn-primary mt-4"
            >
              Create New Calculation
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Showing {filteredScenarios.length} of {scenarios.length} calculations
            </p>

            <div className="grid grid-cols-1 gap-4">
              {filteredScenarios.map((scenario) => (
                <div key={scenario.id} className="card p-4 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-slate-900">
                          {scenario.name || 'Untitled Calculation'}
                        </h3>
                        {getStatusBadge(scenario.status)}
                      </div>
                      {scenario.collection_name && (
                        <p className="text-xs text-indigo-600 font-medium mb-2" data-testid={`proposal-title-${scenario.id}`}>
                          Proposal: {scenario.collection_name}
                        </p>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Service</p>
                          <p className="font-medium text-slate-900">{scenario.service_name || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Created</p>
                          <p className="font-medium text-slate-900 flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(scenario.created_at)}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-500">Updated</p>
                          <p className="font-medium text-slate-900">{formatDate(scenario.updated_at)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Created By</p>
                          <p className="font-medium text-slate-900">{scenario.created_by || 'You'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleView(scenario.id)}
                        className="btn-secondary flex items-center gap-2"
                        title="View/Edit calculation"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </button>
                      {(scenario.status === 'draft' || userRole === 'admin') && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(scenario.id, scenario.name); }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete calculation"
                          data-testid={`delete-btn-${scenario.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  Delete Calculation
                </h3>
                <p className="text-sm text-slate-600">
                  Are you sure you want to delete <strong>"{deleteModal.scenario?.name}"</strong>?
                </p>
                <p className="text-sm text-red-600 mt-2">
                  This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteModal({ show: false, scenario: null })}
                disabled={deleting}
                className="px-4 py-2 text-sm text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
